// packages
import moment from "moment"
import Encoding from "encoding-japanese"

// types
import { ITableDataType } from "../types"

export const extractDayAsPerMonth = (year: number, month: number) => {
  const yearMonth = moment(`${year}-${month}`)
  const days = []
  const daysInMonth = yearMonth.daysInMonth()
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i)
  }
  return days.map((day) => ({
    label: day,
    value: day,
  }))
}

const applyCharLimit = (str: string, limit: number) => {
  if (str?.length < limit) {
    return str
  }
  return str.substring(0, limit)
}

const fullToHalfKanaMap = {
  "。": "｡",
  "「": "｢",
  "」": "｣",
  "、": "､",
  "・": "･",
  ー: "ｰ",
  ァ: "ｧ",
  ア: "ｱ",
  ィ: "ｨ",
  イ: "ｲ",
  ゥ: "ｩ",
  ウ: "ｳ",
  ェ: "ｪ",
  エ: "ｴ",
  ォ: "ｫ",
  オ: "ｵ",
  カ: "ｶ",
  ガ: "ｶﾞ",
  キ: "ｷ",
  ギ: "ｷﾞ",
  ク: "ｸ",
  グ: "ｸﾞ",
  ケ: "ｹ",
  ゲ: "ｹﾞ",
  コ: "ｺ",
  ゴ: "ｺﾞ",
  サ: "ｻ",
  ザ: "ｻﾞ",
  シ: "ｼ",
  ジ: "ｼﾞ",
  ス: "ｽ",
  ズ: "ｽﾞ",
  セ: "ｾ",
  ゼ: "ｾﾞ",
  ソ: "ｿ",
  ゾ: "ｿﾞ",
  タ: "ﾀ",
  ダ: "ﾀﾞ",
  チ: "ﾁ",
  ヂ: "ﾁﾞ",
  ッ: "ｯ",
  ツ: "ﾂ",
  ヅ: "ﾂﾞ",
  テ: "ﾃ",
  デ: "ﾃﾞ",
  ト: "ﾄ",
  ド: "ﾄﾞ",
  ナ: "ﾅ",
  ニ: "ﾆ",
  ヌ: "ﾇ",
  ネ: "ﾈ",
  ノ: "ﾉ",
  ハ: "ﾊ",
  バ: "ﾊﾞ",
  パ: "ﾊﾟ",
  ヒ: "ﾋ",
  ビ: "ﾋﾞ",
  ピ: "ﾋﾟ",
  フ: "ﾌ",
  ブ: "ﾌﾞ",
  プ: "ﾌﾟ",
  ヘ: "ﾍ",
  ベ: "ﾍﾞ",
  ペ: "ﾍﾟ",
  ホ: "ﾎ",
  ボ: "ﾎﾞ",
  ポ: "ﾎﾟ",
  マ: "ﾏ",
  ミ: "ﾐ",
  ム: "ﾑ",
  メ: "ﾒ",
  モ: "ﾓ",
  ャ: "ｬ",
  ヤ: "ﾔ",
  ュ: "ｭ",
  ユ: "ﾕ",
  ョ: "ｮ",
  ヨ: "ﾖ",
  ラ: "ﾗ",
  リ: "ﾘ",
  ル: "ﾙ",
  レ: "ﾚ",
  ロ: "ﾛ",
  ワ: "ﾜ",
  ヲ: "ｦ",
  ン: "ﾝ",
  ヴ: "ｳﾞ",
  ヵ: "ｶ",
  ヶ: "ｹ",
}

const convertFullWidthToHalfWidth = (str) => {
  if (!str || typeof str != "string") return str

  const orginalLen = str?.length
  const converted = str
    .toString()
    .replace(/[\uFF01-\uFF5E]/g, (ch) =>
      String.fromCharCode(ch.charCodeAt(0) - 0xfee0)
    )
    .replace(/\u3000/g, " ")
    .split("")
    .map((c) => fullToHalfKanaMap?.[c] || c)
    .join("")

  if (orginalLen == converted.length) return converted
  return converted.substring(0, orginalLen)
}

export const getHeaderRowForZenginFormat = (
  facilityInfo,
  format: "JIS" | "EBCDIC",
  withdrawalMonth,
  withrawalDay,
  forText = false
) => {
  return [
    // Data classification fixed to value 1
    1,
    // Type code fixed to value 91,
    91,
    // Code classification 0: JIS, 1: EBCDIC
    format == "JIS" ? 0 : 1,
    // Consignor code from facility 10 digit right justified by 0
    forText
      ? applyCharLimit(
          (
            facilityInfo?.debit_account_transfer_consign_no?.toString() || "0"
          )?.padStart(10, "0"),
          10
        )
      : +facilityInfo?.debit_account_transfer_consign_no,
    // Cosignor name from facility 40 character left aligned by space
    applyCharLimit(
      (facilityInfo?.consign_name_katakana || "")?.padEnd(40, " "),
      40
    ),
    // withdrawal date in MMDD Format
    withdrawalMonth?.toString()?.padStart(2, "0") +
      withrawalDay?.toString()?.padStart(2, "0"),
    // facility bank number 4 char
    forText
      ? (facilityInfo?.bank_no || "")?.padStart(4, "0")
      : +facilityInfo?.bank_no,
    // facility bank name kana 15 char left aligned by space
    applyCharLimit(
      (facilityInfo?.financial_institution_name_katakana || "")?.padEnd(
        15,
        " "
      ),
      15
    ),
    // facility branch number 3 char
    forText
      ? (facilityInfo?.branch_no || "")?.padStart(3, "0")
      : +facilityInfo?.branch_no,
    // facility branch name kana 15 char
    applyCharLimit(
      (facilityInfo?.branch_name_katakana || "")?.padEnd(15, " "),
      15
    ),
    // facility account type 1: Normal, 2: Current, 9: Other
    facilityInfo?.account_type == 2
      ? 1
      : facilityInfo?.account_type == 1
      ? 2
      : 9,
    // facility account number 7 char right justified by 0
    forText
      ? (facilityInfo?.account_number || "")?.padStart(7, "0")
      : facilityInfo?.account_number,
    // dummy area
    " ".padEnd(17, " "),
  ].map((d) => convertFullWidthToHalfWidth(d))
}

export const getDataForCSV = (
  data: ITableDataType[][],
  forText = false
): any[] => {
  let childCount = 0
  let totalBilling = 0
  const userData = []
  data?.forEach((item) => {
    const newArr = item
      .filter((filterItem) => filterItem.checked)
      .map((itm) => {
        childCount++
        totalBilling += itm?.billed_amount || 0
        return [
          // Data classification static value to 2
          2,
          // parent bank no 4 digit
          forText
            ? itm?.parent_bank_no?.padStart(4, "0")
            : +itm?.parent_bank_no,
          // parent bank name 15 char left aligned with space
          applyCharLimit(
            itm?.parent_financial_institution_name?.padEnd(15, " "),
            15
          ),
          // branch number 3 digit
          forText
            ? itm?.parent_branch_no?.padStart(3, "0")
            : +itm?.parent_branch_no,
          // parent branch name 15 char left aligned with space
          applyCharLimit(itm?.parent_branch_name_katakana?.padEnd(15, " "), 15),
          // dummy area 4 empty space
          " ".padStart(4, " "),
          // parent account type 1: Normal, 2: Current, 3: Tax reserve, 9: other
          itm?.parent_account_type < 4 ? itm?.parent_account_type : 9,
          // parent account number 7 digit right aligh with 0
          forText
            ? itm?.parent_account_number?.padStart(7, "0")
            : +itm?.parent_account_number,
          // parent account name 30 char left aligned with space
          applyCharLimit(itm?.parent_account_holder_name?.padEnd(30, " "), 30),
          // withdrawal amount 10 digit right aligned with 0
          forText
            ? itm?.billed_amount?.toString()?.padStart(10, "0")
            : itm?.billed_amount,
          // static 1
          1,
          // customer code 20 left aligned with 0
          forText
            ? applyCharLimit(itm?.parent_customer_number?.padStart(20, "0"), 20)
            : BigInt(itm?.parent_customer_number),
          // static 0
          0,
          // dummy area 8 char space
          " ".padStart(8, " "),
        ].map((d) => convertFullWidthToHalfWidth(d))
      })
    if (newArr.length > 0) {
      userData.push(newArr)
    }
  })

  const trailerRecord = [
    // data classification static 8
    8,
    // total number of child, 6 digit right aligned with 0
    forText ? childCount?.toString()?.padStart(6, "0") : childCount,
    // total amount, 12 digit right aligned with 0
    forText ? totalBilling?.toString()?.padStart(12, "0") : totalBilling,
    // number of transfers completed, static 0 of 6 digit,
    forText ? "0".padStart(6, "0") : 0,
    // transferred amount, static 0 of 12 digits
    forText ? "0".padStart(12, "0") : 0,
    // number of cases that cannot be completed, static 0 of 6 digits
    forText ? "0".padStart(6, "0") : 0,
    // non transferable amount, static 0 of 12 digits
    forText ? "0".padStart(12, "0") : 0,
    // dummy space 65 char space
    " ".padEnd(65, " "),
  ]

  userData.push([trailerRecord])

  const endRecord = [
    // data classification static 9
    9,
    // dummy character 119 space
    " ".padEnd(119, " "),
  ]

  userData.push([endRecord])
  return userData
}

export { formatOutputTableMapper } from "./mapper"

export const ConvertDataToStringForEncoding = (
  tableDataInArray: any[][],
  joiner = ","
) => {
  const arrayToString = tableDataInArray
    .filter((row) => row?.length)
    .map((i) => {
      return i
        .map((j) => (Array.isArray(j) ? j.join(joiner) + "\r\n" : j))
        .join("")
    })
    .join("")

  return arrayToString
}

// Convert table data to JIS
export const TableDataToJIS = (tableDataInArray: any[][], joiner = ",") => {
  const arrayToString = tableDataInArray
    .map((i) => {
      return i
        .map((j) => (Array.isArray(j) ? j.join(joiner) + "\r\n" : j))
        .join("")
    })
    .join("")

  const unicodeArray = Encoding.stringToCode(arrayToString) // Convert string to code array

  const jisArray = Encoding.convert(unicodeArray, {
    to: "JIS",
    from: "UNICODE",
  })

  const backToUnicode = Encoding.convert(jisArray, {
    to: "UNICODE",
    from: "JIS",
  })

  return Encoding.codeToString(backToUnicode)
}

// Convert table data to EBCDIC
export const TableDataToEBCDIC = (tableDataInArray: any[][], joiner = ",") => {
  const arrayToString = tableDataInArray
    .map((i) => {
      return i
        .map((j) => (Array.isArray(j) ? j.join(joiner) + "\r\n" : j))
        .join("")
    })
    .join("")

  return arrayToString
}
